import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Button, ButtonSize, ButtonType, Dropdown, IconSize, IDropdownItem, Input, Tag, Tags} from "@renta-apps/renta-react-components";
import Localizer from "@/localization/Localizer";
import styles from "./FiltersAndSort.module.scss";
import {useLocalStorage} from "@/helpers/Hooks";

interface IFiltersAndSortProps {
    onGetReportModalOpen: () => void;
    sortBy: string;
    sortOrder: string;
    deviceNames: string[];
    setSortBy: (value: string) => void;
    setSortOrder: (value: string) => void;
    setDeviceNames: (value: string[]) => void;
}

enum FilterType {
    DEVICE_NAMES = "device_names",
}

const FiltersAndSort: React.FC<IFiltersAndSortProps> = (props) => {
    const [deviceName, setDeviceName] = useState<string>('');
    const [deviceNameSuggestions, setDeviceNameSuggestions] = useLocalStorage<string[]>("renta.equipments.deviceName.suggestions", []);
    const [combinedFilterValues, setCombinedFilterValues] = useState<Tag[]>([]);

    const hasTags = useMemo(() => combinedFilterValues.length > 0, [combinedFilterValues]);

    useEffect(() => {
        setCombinedFilterValues([
            ...props.deviceNames.map((item) => (
                {id: item, label: item ?? '', tooltip: Localizer.fleetMonitoringPageFiltersTooltipNameOrId, type: FilterType.DEVICE_NAMES}
            )),
        ]);
    }, [props.deviceNames]);

    const sortOrderItems: IDropdownItem[] = [
        {name: Localizer.enumSortDirectionAsc, value: "Asc"},
        {name: Localizer.enumSortDirectionDesc, value: "Desc"},
    ];

    const sortByItems: IDropdownItem[] = [
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByName, value: "Name"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByProductGroup, value: "ProductGroupName"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByCategory, value: "CategoryName"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByRentalStartDate, value: "RentalStartDate"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByRentalEndDate, value: "RentalEndDate"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByIdle, value: "IdleDays"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByAlerts, value: "AlertsCount"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByBattery, value: "BatteryLevel"},
        {name: Localizer.constructionSiteDetailsEquipmentTabSortByFuel, value: "FluidLevel"},
    ];

    const onFiltersReset = () => {
        props.setDeviceNames([]);
    };

    const toggleDeviceNamesItem = (label: string) => {
        if (props.deviceNames.some((name) => name === label)) {
            props.setDeviceNames(props.deviceNames.filter((name) => name !== label));
        } else {
            props.setDeviceNames([...props.deviceNames, label]);
        }
    };

    const toggleFilterItem = (tag: Tag) => {
        const tagType = tag.type;
        const tagName = tag.label;
        switch (tagType) {
            case FilterType.DEVICE_NAMES:
                toggleDeviceNamesItem(tagName);
                break;
            default:
                console.error(`Unknown tag type: ${tagType}`);
        }
    };

    const formatSelectedLabel = (selectedCount: number) => {
        return Localizer.get(Localizer.componentDropdownMultipleSelectedLanguageItemName, selectedCount);
    };

    const getDeviceNameSuggestions = useCallback((value: string) => {
        let suggestions = [...deviceNameSuggestions];
        if (value) {
            suggestions = suggestions.filter(item => item.toLowerCase().includes(value.toLowerCase()));
        }

        return suggestions.map(item => ({name: item, value: item}));
    }, [deviceNameSuggestions]);

    const handleFormSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        const trimmedDeviceName = deviceName.trim();
        if (!trimmedDeviceName) {
            return;
        }
        setDeviceNameSuggestions(prev => [trimmedDeviceName, ...prev.filter(item => item !== trimmedDeviceName)].slice(0, 5));
        props.setDeviceNames([...(new Set([...props.deviceNames, trimmedDeviceName]))]);
        setDeviceName('');
        (document.activeElement as HTMLInputElement)?.blur();
    };

    return (
        <div className={styles.container}>
            <form className={styles.searchRow} onSubmit={handleFormSubmit}>
                <div className={`${styles.deviceName}`}>
                    <Input id="filter-equipments-by-name"
                           value={deviceName}
                           label={Localizer.fleetMonitoringPageFiltersLabelsName}
                           inputProperties={{
                               name: 'deviceName',
                               placeholder: props.deviceNames.length ? formatSelectedLabel(props.deviceNames.length) : undefined,
                               autoComplete: 'off'
                           }}
                           onChange={setDeviceName}
                           useSuggestions
                           minValueLengthForSuggestions={0}
                           getSuggestionsDelayMs={0}
                           getSuggestions={(value: string) => getDeviceNameSuggestions(value)}
                           onSuggestionClick={(item: IDropdownItem) => {
                               setDeviceName('');
                               props.setDeviceNames([...(new Set([...props.deviceNames, item.value!.toString()]))]);
                           }}
                    />
                </div>
                <div className={`${styles.search}`}>
                    <Button className={styles.buttonWithIcon}
                            type={ButtonType.Primary}
                            size={ButtonSize.Small}
                            id={"search-equipments-button"}
                            iconLeft={{name: "magnifyingGlass", color: "white", size: IconSize.Medium}}
                            submit
                    >
                        {Localizer.searchTitle}
                    </Button>
                </div>
            </form>
            {hasTags && (
                <div className={styles.filterTags}>
                    {combinedFilterValues.length ?
                        <Tags className={styles.tagsWrapper}
                              items={combinedFilterValues}
                              onClick={(tag: Tag) => toggleFilterItem(tag)}
                              onDelete={(tag: Tag) => toggleFilterItem(tag)}
                              onDeleteAll={onFiltersReset}
                              localizations={{deleteAllTagsTitle: Localizer.fleetMonitoringPageFiltersTagsRemoveAll}}
                        /> : null
                    }
                </div>
            )}
            <div className={styles.sortRow}>
                <Dropdown id="equipments-sort-by"
                          items={sortByItems}
                          selectedItem={props.sortBy}
                          className={styles.formField}
                          label={Localizer.fleetMonitoringPageFiltersLabelsSortBy}
                          onChange={(item: IDropdownItem) => props.setSortBy(item.value!.toString())}
                />
                <Dropdown id="equipments-sort-order"
                          items={sortOrderItems}
                          selectedItem={props.sortOrder}
                          className={styles.formField}
                          label={Localizer.fleetMonitoringPageFiltersLabelsSortOrder}
                          onChange={(item: IDropdownItem) => props.setSortOrder(item.value!.toString())}
                />
                <div className={styles.buttonContainer}>
                    <Button className={styles.getReportButton}
                            type={ButtonType.Secondary}
                            size={ButtonSize.Small}
                            id={"getReportButton"}
                            iconLeft={{name: "download", color: "#FE5000", size: IconSize.Medium}}
                            onClick={props.onGetReportModalOpen}
                    >
                        {Localizer.constructionSiteDetailsEquipmentTabGetReportButton}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default FiltersAndSort;